import React, { useEffect, useMemo, useState } from "react";
import PageShell from "@/components/PageShell";
import BrandCard from "@/components/BrandCard";
import { api } from "@/lib/api";
import { CONTACT, waLink } from "@/lib/brand";
import { Search, Store, PhoneCall, MessageCircle } from "lucide-react";

export default function Brands() {
    const [items, setItems] = useState(null);
    const [meta, setMeta] = useState({ categories: [], opportunity_types: [] });
    const [cat, setCat] = useState("All");
    const [opp, setOpp] = useState("All");
    const [q, setQ] = useState("");

    useEffect(() => {
        api.get("/brands", { params: { limit: 200 } }).then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
        api.get("/brands/meta").then((r) => setMeta(r.data)).catch(() => {});
    }, []);

    const usedCats = useMemo(() => meta.categories.filter((c) => (items || []).some((b) => b.category === c)), [meta, items]);
    const shown = useMemo(() => (items || [])
        .filter((b) => cat === "All" || b.category === cat)
        .filter((b) => opp === "All" || b.opportunity_type === opp)
        .filter((b) => !q || `${b.name} ${b.tagline || ""} ${b.category}`.toLowerCase().includes(q.toLowerCase())),
    [items, cat, opp, q]);

    return (
        <PageShell navVariant="dark">
            <section className="bg-[#0a0d18] text-white pt-32 pb-16" data-testid="brands-hero">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10 items-end">
                    <div className="lg:col-span-8">
                        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] font-bold text-[var(--ebg-gold)]"><Store size={13}/> All Brands</div>
                        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-4">Brands looking for<br/><em className="not-italic text-[var(--ebg-gold)]">their next address.</em></h1>
                        <p className="text-white/70 mt-5 max-w-xl leading-relaxed text-sm md:text-base">Franchise, lease and partnership opportunities with established brands — matched with the right commercial space by EBG Realty.</p>
                    </div>
                    <div className="lg:col-span-4 border border-white/10 bg-white/[0.03] p-5">
                        <div className="text-[11px] uppercase tracking-[0.2em] font-bold">Own a commercial space or want a franchise?</div>
                        <div className="text-white/60 text-[13px] mt-1">Talk to our commercial desk.</div>
                        <div className="grid grid-cols-2 gap-2 mt-4">
                            <a href={CONTACT.tel} data-testid="brands-hero-call" className="bg-[var(--ebg-gold)] text-black py-3 text-[10px] uppercase tracking-[0.2em] font-bold inline-flex items-center justify-center gap-2 hover:bg-white"><PhoneCall size={12}/> Call</a>
                            <a href={waLink("Hi EBG Realty, I'd like to know more about brand / franchise opportunities.")} target="_blank" rel="noopener noreferrer" data-testid="brands-hero-whatsapp" className="border border-white/30 py-3 text-[10px] uppercase tracking-[0.2em] font-bold inline-flex items-center justify-center gap-2 hover:bg-emerald-600 hover:border-emerald-600"><MessageCircle size={12}/> WhatsApp</a>
                        </div>
                    </div>
                </div>
            </section>

            <section className="max-w-[1440px] mx-auto px-6 md:px-10 py-12" data-testid="brands-list">
                <div className="flex flex-wrap gap-2 mb-5">
                    {["All", ...usedCats].map((c) => (
                        <button key={c} onClick={() => setCat(c)} data-testid={`brand-cat-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                            className={`px-4 py-2 text-[11px] uppercase tracking-[0.18em] font-bold transition-colors ${cat === c ? "bg-black text-white" : "border border-black/15 text-black/60 hover:text-black hover:border-black"}`}>
                            {c}
                        </button>
                    ))}
                </div>
                <div className="flex flex-col sm:flex-row gap-3 mb-10">
                    <label className="flex-1 flex items-center gap-2 border border-black/15 bg-white px-4">
                        <Search size={14} className="text-black/40"/>
                        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search brands" data-testid="brands-search" className="flex-1 py-2.5 text-sm outline-none bg-transparent"/>
                    </label>
                    <select value={opp} onChange={(e) => setOpp(e.target.value)} data-testid="brands-filter-opportunity" className="border border-black/15 bg-white px-4 py-2.5 text-[12px] font-medium outline-none">
                        {["All", ...meta.opportunity_types].map((o) => <option key={o} value={o}>{o === "All" ? "All Opportunity Types" : o}</option>)}
                    </select>
                </div>
                {items === null ? (
                    <div className="text-black/40 py-20 text-center">Loading brands…</div>
                ) : shown.length === 0 ? (
                    <div className="border border-dashed border-black/15 py-20 text-center text-black/55" data-testid="brands-empty">No brands match these filters yet.</div>
                ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {shown.map((b) => <BrandCard key={b.id} brand={b}/>)}
                    </div>
                )}
            </section>
        </PageShell>
    );
}
